import { useEffect, useState } from 'react';
import { getHealthSafetyData, saveFireSafetyItem, deleteFireSafetyItem, GetHealthSafetyDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Flame, CheckCircle2, AlertTriangle, XCircle, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';

type Item = GetHealthSafetyDataOutputType['items'][0];
type EditItem = Partial<Item> & { roomId?: string };

const STATUS_CONFIG = {
  Good: { color: 'bg-green-500/10 text-green-700 border-green-500/30', icon: CheckCircle2 },
  'Due Soon': { color: 'bg-yellow-500/10 text-yellow-700 border-yellow-500/30', icon: Clock },
  Overdue: { color: 'bg-orange-500/10 text-orange-700 border-orange-500/30', icon: AlertTriangle },
  Faulty: { color: 'bg-red-500/10 text-red-700 border-red-500/30', icon: XCircle },
};

const TYPES = ['Fire Extinguisher', 'Fire Exit', 'First Aid Kit', 'Assembly Point', 'Fire Alarm Panel', 'Emergency Light', 'Fire Hose Reel', 'Smoke Detector', 'Fire Blanket', 'Eye Wash Station'];
const STATUSES = ['Good', 'Due Soon', 'Overdue', 'Faulty'];

const fmtDate = (d?: string) => { try { return d ? format(parseISO(d), 'dd MMM yyyy') : '—'; } catch { return d || '—'; } };

export default function FireSafetyAdminTab({ rooms }: { rooms: { id: string; name: string }[] }) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<EditItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState('All');

  const load = () => {
    setLoading(true);
    getHealthSafetyData({ includeInactive: true }).then(d => setItems(d.items)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = filterStatus === 'All' ? items : items.filter(i => i.status === filterStatus);

  const grouped = filtered.reduce((acc, item) => {
    const type = item.type || 'Other';
    if (!acc[type]) acc[type] = [];
    acc[type].push(item);
    return acc;
  }, {} as Record<string, Item[]>);

  const handleSave = async () => {
    if (!editing?.itemName?.trim()) { toast.error('Item name is required'); return; }
    setSaving(true);
    try {
      await saveFireSafetyItem({ ...editing as any, itemName: editing.itemName! });
      toast.success(editing.id ? 'Updated' : 'Added');
      setEditing(null);
      load();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    try { await deleteFireSafetyItem({ id }); toast.success('Deleted'); load(); }
    catch (e: any) { toast.error(e.message); }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14" />)}</div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          {['All', ...STATUSES].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filterStatus === s ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}>
              {s}
            </button>
          ))}
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setEditing({ itemName: '', type: 'Fire Extinguisher', status: 'Good', active: true })}>
          <Plus className="w-3.5 h-3.5" /> Add Item
        </Button>
      </div>

      {editing && (
        <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/20">
          <p className="font-semibold text-sm text-foreground">{editing.id ? 'Edit Item' : 'New Fire Safety Item'}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Item Name *</label>
              <Input value={editing.itemName || ''} onChange={e => setEditing(s => ({ ...s!, itemName: e.target.value }))} placeholder="e.g. CO2 Extinguisher — Bar" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Type</label>
              <Select value={editing.type || 'none'} onValueChange={v => setEditing(s => ({ ...s!, type: v === 'none' ? undefined : v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Room</label>
              <Select value={editing.roomId || editing.roomId || 'none'} onValueChange={v => setEditing(s => ({ ...s!, roomId: v === 'none' ? undefined : v }))}>
                <SelectTrigger><SelectValue placeholder="Select room" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific room</SelectItem>
                  {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Status</label>
              <Select value={editing.status || 'Good'} onValueChange={v => setEditing(s => ({ ...s!, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Last Inspected</label>
              <Input type="date" value={editing.lastInspected || ''} onChange={e => setEditing(s => ({ ...s!, lastInspected: e.target.value || undefined }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Next Inspection</label>
              <Input type="date" value={editing.nextInspection || ''} onChange={e => setEditing(s => ({ ...s!, nextInspection: e.target.value || undefined }))} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Serial Number</label>
              <Input value={editing.serialNumber || ''} onChange={e => setEditing(s => ({ ...s!, serialNumber: e.target.value || undefined }))} placeholder="Optional" />
            </div>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Location Description</label>
            <Input value={editing.locationDescription || ''} onChange={e => setEditing(s => ({ ...s!, locationDescription: e.target.value }))} placeholder="e.g. Near main entrance, left of door" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Notes</label>
            <Textarea value={editing.notes || ''} onChange={e => setEditing(s => ({ ...s!, notes: e.target.value }))} rows={2} />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving}>{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
          </div>
        </div>
      )}

      {Object.keys(grouped).length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-12 text-center text-muted-foreground">
          <Flame className="w-8 h-8 mx-auto mb-3 opacity-30" />
          <p className="text-sm">No fire safety items added yet.</p>
        </div>
      ) : (
        (Object.entries(grouped) as [string, Item[]][]).map(([type, typeItems]) => (
          <div key={type}>
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">{type} ({typeItems.length})</p>
            <div className="space-y-2">
              {typeItems.map(item => {
                const cfg = STATUS_CONFIG[item.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.Good;
                const StatusIcon = cfg.icon;
                return (
                  <div key={item.id} className="border border-border rounded-lg p-3 flex items-center gap-3">
                    <StatusIcon className={`w-4 h-4 shrink-0 ${item.status === 'Good' ? 'text-green-600' : item.status === 'Due Soon' ? 'text-yellow-600' : item.status === 'Overdue' ? 'text-orange-600' : 'text-red-600'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{item.itemName}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {item.roomName && <span>{item.roomName} · </span>}
                        {item.locationDescription && <span>{item.locationDescription} · </span>}
                        Inspected: {fmtDate(item.lastInspected)} · Next: {fmtDate(item.nextInspection)}
                      </p>
                    </div>
                    <Badge variant="outline" className={`text-xs shrink-0 ${cfg.color}`}>{item.status}</Badge>
                    <div className="flex gap-1 shrink-0">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setEditing({ ...item, roomId: item.roomId })}><Pencil className="w-3.5 h-3.5" /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader><AlertDialogTitle>Delete "{item.itemName}"?</AlertDialogTitle></AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(item.id)} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}
    </div>
  );
}
